import type { DocField, FieldValue } from './field-kind';
import { readFields, type FieldBinding } from './field-reader';
import { writeValue } from './field-writer';

interface Prompt {
  readonly label: string;
  readonly placeholder: string;
}

export class FieldSet {
  private readonly part: Document;
  private readonly prompts = new Map<string, Prompt>();
  private readonly touched = new Set<string>();
  private bindings: FieldBinding[];

  private constructor(part: Document) {
    this.part = part;
    this.bindings = readFields(part);

    for (const { field } of this.bindings) {
      this.prompts.set(field.id, { label: field.label, placeholder: field.placeholder });
    }
  }

  static read(part: Document): FieldSet {
    return new FieldSet(part);
  }

  fields(): readonly DocField[] {
    return this.bindings.map((b) => this.present(b.field));
  }

  setValue(id: string, value: FieldValue): void {
    const binding = this.bindings.find((b) => b.field.id === id);
    if (!binding) {
      throw new Error(`Unknown field: ${id}`);
    }

    writeValue(binding, value);
    this.touched.add(id);
    this.bindings = readFields(this.part);
  }

  private present(field: DocField): DocField {
    const prompt = this.prompts.get(field.id);
    if (!prompt) {
      return field;
    }

    const showsHint = !!prompt.placeholder && !this.touched.has(field.id);

    return {
      ...field,
      label: prompt.label,
      placeholder: showsHint ? prompt.placeholder : '',
      value: showsHint ? '' : field.value,
    };
  }
}
